import classnames from "classnames";
import React from "react";
import { Hyperlink, Icon } from "src/components";
import { getProductLabel } from "src/services/product.service";
import { translate } from "src/services/translation.service";
import { media } from "src/theme";
import { IProduct } from "src/types/product";
import styled from "styled-components";

import { handleWishListClick } from "../helpers/wishList";
import { ILayout, ITheme, IUserWish } from "../types";

import { ProductImage, ProductPromotion, ProductReward } from ".";

const WishlistIcon = styled(Icon)<ITheme>`
  position: absolute;
  top: 16px;
  right: ${({ theme }: ITheme): string =>
    theme === "mini" || theme === "newArrivals" ? "0px" : "16px"};
  padding: 4px;
  background-color: transparent;
  border: none;
`;

const Wrapper = styled.div<ILayout & ITheme>`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  order: ${({ theme }: ITheme): number | string =>
    theme === "mini" || theme === "newArrivals" ? -1 : "initial"};
  position: relative;

  ${WishlistIcon} {
    ${({ layout }: ILayout): string =>
      layout === "horizontal" ? "top: -12px; right: 32px;" : ""};
  }

  ${media.tablet} {
    height: 100%;
  }
`;

interface ILeftWrapper extends ITheme {
  className?: string;
}

const LeftWrapper = styled.div<ILeftWrapper>`
  position: absolute;
  top: 16px;
  left: ${({ theme }: ITheme): string =>
    theme === "mini" || theme === "newArrivals" ? "-8px" : "16px"};
`;

const OutOfStockBlock = styled.div`
  position: absolute;
  letter-spacing: 2.1px;
  text-transform: uppercase;
  background-color: rgba(255, 255, 255, 0.4);
  height: 26px;
  align-items: center;
  display: flex;
  width: 100%;
  justify-content: center;
`;

const Labels = styled.div`
  position: absolute;
  left: 0;
  transform-origin: center top;
  transform: translate(-50%, 50%) rotate(-90deg);

  span {
    text-transform: uppercase;
    letter-spacing: 2.1px;
  }
`;

interface IProps extends ITheme {
  product: IProduct;
  isOutOfStockClassname: { "out-of-stock": boolean };
  outOfStock: boolean;
  isWished: boolean;
  isAuthenticated: boolean;
  userWishAdd: IUserWish;
  userWishRemove: IUserWish;
  handleClick?: () => void;
  imageMaxHeight?: number;
  hasNoProductLink?: boolean;
  url?: string;
}

const ImageWrapper = ({
  product,
  isOutOfStockClassname,
  outOfStock,
  isWished,
  isAuthenticated,
  userWishAdd,
  userWishRemove,
  handleClick,
  imageMaxHeight,
  hasNoProductLink,
  theme,
  url,
}: IProps): JSX.Element => {
  return (
    <Wrapper className="productImageWrapper" theme={theme}>
      <LeftWrapper className="productLeftLabel">
        <ProductReward rewards={product.rewards} />
        <ProductPromotion promotion={product.promotion} />
      </LeftWrapper>
      {outOfStock && (
        <OutOfStockBlock>
          {product.availabilityLabel || translate("product.outOfStock")}
        </OutOfStockBlock>
      )}
      <Hyperlink to={hasNoProductLink ? undefined : url}>
        <ProductImage
          isOutOfStockClassname={isOutOfStockClassname}
          product={product}
          imageMaxHeight={imageMaxHeight}
          handleClick={handleClick}
          theme={theme}
        >
          <WishlistIcon
            name={isWished ? "coeur-plein" : "coeur"}
            size={21}
            onClick={(): Promise<void> | void => {
              handleWishListClick(
                isWished!,
                product,
                isAuthenticated,
                userWishAdd,
                userWishRemove
              );
            }}
            className={classnames("wishListButton", isOutOfStockClassname)}
            theme={theme}
          />
        </ProductImage>
      </Hyperlink>

      <Labels className="productImageLabel">
        <span>{getProductLabel(product)}</span>
      </Labels>
      <WishlistIcon
        name={isWished ? "coeur-plein" : "coeur"}
        size={21}
        onClick={(): Promise<void> | void => {
          handleWishListClick(
            isWished!,
            product,
            isAuthenticated,
            userWishAdd,
            userWishRemove
          );
        }}
        className={classnames("wishListButton", isOutOfStockClassname)}
        theme={theme}
      />
    </Wrapper>
  );
};

export default ImageWrapper;
